import { Injectable, inject } from '@angular/core';
import {
  RequestNewMobileDocument,
  RequestNewMobileMutationVariables,
  RequestUpdateMobileDocument,
  RequestUpdateMobileMutationVariables,
  ValidateNewMobileDocument,
  ValidateNewMobileMutationVariables,
  ValidateUpdateMobileDocument,
  ValidateUpdateMobileMutationVariables,
} from '@shared/graphql/account.generated';
import { Status } from '@shared/graphql/api.generated';
import {
  ConfirmParentalCodeDocument,
  ConfirmParentalCodeQueryVariables,
  CreateProfileDocument,
  CreateProfileMutationVariables,
  DeleteProfileDocument,
  DeleteProfileMutationVariables,
  GetProfileImageGroupsDocument,
  GetProfileImageGroupsOutput,
  LoginOutput,
  SelectProfileDocument,
  SelectProfileOutput,
  SelectProfileQueryVariables,
  UpdateProfileDocument,
  UpdateProfileMutationVariables,
} from '@shared/graphql/auth.generated';
import { GraphqlClients, GraphqlService } from '@shared/graphql/graphql.service';
import { TokenService } from '@shared/services/token.service';
import { SHA256 } from 'crypto-js';
import { GeneralService } from './general.service';

@Injectable()
export class UserService {
  private readonly graphqlService = inject(GraphqlService);
  private readonly tokenService = inject(TokenService);
  private readonly generalService = inject(GeneralService);

  createProfile(form: any) {
    return this.graphqlService.makeMutation<LoginOutput, CreateProfileMutationVariables>(
      GraphqlClients.auth,
      CreateProfileDocument,
      this.generalService.deepClean(form) as CreateProfileMutationVariables,
    );
  }

  updateProfile(id: string, form: any) {
    const body: UpdateProfileMutationVariables = {
      id,
      ...form,
    };
    return this.graphqlService.makeMutation<LoginOutput, UpdateProfileMutationVariables>(
      GraphqlClients.auth,
      UpdateProfileDocument,
      this.generalService.deepClean(body) as UpdateProfileMutationVariables,
    );
  }

  deleteProfile(id: string) {
    return this.graphqlService.makeMutation<LoginOutput, DeleteProfileMutationVariables>(
      GraphqlClients.auth,
      DeleteProfileDocument,
      {
        id,
      },
    );
  }

  selectProfile(id: string) {
    return this.graphqlService.makeQuery<SelectProfileOutput, SelectProfileQueryVariables>(
      GraphqlClients.auth,
      SelectProfileDocument,
      {
        id,
      },
    );
  }

  getAvatars() {
    return this.graphqlService.makeQuery<GetProfileImageGroupsOutput>(
      GraphqlClients.auth,
      GetProfileImageGroupsDocument,
    );
  }

  confirmParentalCode(id: string, code: string) {
    const activeProfileId = this.tokenService.userInfo()?.activeProfile.id;
    return this.graphqlService.makeQuery<SelectProfileOutput, ConfirmParentalCodeQueryVariables>(
      GraphqlClients.auth,
      ConfirmParentalCodeDocument,
      {
        id,
        confirmationCode: SHA256(code + activeProfileId).toString(),
      },
    );
  }

  requestUpdateUserMobile() {
    return this.graphqlService.makeMutation<Status, RequestUpdateMobileMutationVariables>(
      GraphqlClients.account,
      RequestUpdateMobileDocument,
    );
  }

  validateUpdateMobile(token: string) {
    return this.graphqlService.makeMutation<Status, ValidateUpdateMobileMutationVariables>(
      GraphqlClients.account,
      ValidateUpdateMobileDocument,
      {
        token,
      },
    );
  }

  requestNewMobile(newMobile: string, secret: string) {
    return this.graphqlService.makeMutation<Status, RequestNewMobileMutationVariables>(
      GraphqlClients.account,
      RequestNewMobileDocument,
      {
        newMobile,
        secret,
      },
    );
  }

  validateNewMobile(token2: string) {
    return this.graphqlService.makeMutation<Status, ValidateNewMobileMutationVariables>(
      GraphqlClients.account,
      ValidateNewMobileDocument,
      {
        token2,
      },
    );
  }
}
